import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { SyntheticEvent, useContext, useEffect, useRef, useState } from 'react';
import postal from 'postal';

import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { usePopper } from 'react-popper';

import { NavigationType, openNewPage } from '../util/navigationHelpers';
import { deleteMemo, getOperationKey, stringsEqualIgnoreCase } from '../util';

import useWindowSize from '../hooks/useWindowSize';
import Menu, { IfmsMenuItem } from '../ui/menu/Menu';

import { FilterStorageVersion, isFilterObjectEmpty } from '../filtering';

import { JobMenuItem } from '../features/jobMenuSlice';
import { moveScreenToTop } from '../features/screenStackSlice';
import constants from '../constants';
import { getOpByName } from "../framework.core/JobNames";
import useJobMenus from "../hooks/useJobMenus";
import JobSwitchingMenuContext from './jobSwitchingMenuContext';
import fastStorage from '../fastStorage';
import useGlobalYesNoDialog from '../hooks/useGlobalYesNoDialog';
import TooltipButton from '../ui/TooltipButton';
import { clearStorage } from '../features/storageSlice';
import { changeOperation, changeSideMenu, clearOperation } from '../features/operationSlice';
import { resetListScreenSettings } from '../features/listScreenSettingsSlice';
import { clearLastSavedJobs } from '../features/lastSavedJobSlice';
import localforage from 'localforage';
import useFunction from '../hooks/useFunction';
import tabListIcon from '../img/job-switching-list.svg';
import tabListIcon_D from '../img/job-switching-list_Dark.svg';
import circleExitRed from '../img/Close01.svg';
import circleExitWhite from '../img/Close01.svg';
import ApplicationContext from '../ApplicationContext';
import { FormType } from '../fms.enum';
import PrimaryButton from '../ui/PrimaryButton';
import useSuntekSettings from '../hooks/useSuntekSettings';
import TaskReminderInfo from '../ui/TaskReminderInfo';
import arrowRightBlueIcon from '../img/job-switching-arrow-right.svg';
import arrowRightSkyBlueIcon from '../img/job-switching-arrow-right-skyblue.svg';
import arrowRightGrayIcon from '../img/job-switching-arrow-right-gray.svg';
import arrowLeftBlueIcon from '../img/job-switching-arrow-left.svg';
import arrowLeftSkyBlueIcon from '../img/job-switching-arrow-left-skyblue.svg';
import arrowLefGrayIcon from '../img/job-switching-arrow-left-gray.svg';

interface JobMenuItemWithIndex extends JobMenuItem {
  index: number,
};

const buttonWidth = 165; // 160 px button + 5px space between

function getActualWidths(renderingButtonCount: number, screenWidth: number, isHome: boolean) {
  const buttonCount = renderingButtonCount;
  const scrollControlsWidth = 307;

  return {
    buttonCount,
    actualWidth : screenWidth,
    scrollControlsWidth,
  };
}

type JobSwitchingMenuProps = {
  resetJobMenuContextState: VoidFunction,
};

const JobSwitchingMenu: React.FC<JobSwitchingMenuProps> = ({ resetJobMenuContextState }) => {
  const chkChannel = postal.channel('ifms');
  const channel = postal.channel('ajax');

  const context = useContext(JobSwitchingMenuContext);
  const appContext = useContext(ApplicationContext);
  const suntekSettings = useSuntekSettings();

  const useDarkMode = appContext.useDarkMode;

  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

  const history = useHistory();
  const dispatch = useAppDispatch();
  const { getSingleOpenedScreenInAnyOperationStartingWithUrl } = useJobMenus();

  const [referenceElement, setReferenceElement] = useState<HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [{ name: 'arrow', options: { element: arrowElement } },],
    placement: 'bottom-end',
  });

  const [mouseOverBtnNum, setMouseOverBtnNum] = useState(-1);
  const [screenWidth] = useWindowSize();
  const [menuHelperVisible, _setMenuHelperVisible] = useState<boolean>(false);
  const [disableJobSwitcgLeftBtn, setDisableJobSwitcgLeftBtn] = useState(true);
  const [disableJobSwitcgRightBtn, setDisableJobSwitcgRightBtn] = useState(false);

  const currentLeftRef = useRef<number>(0);
  const currentScrollPositionRef = useRef<number>(0);

  const currentUrl = `${history.location.pathname}${history.location.search}`;
  const jobMenu = useAppSelector(state => state.jobMenu);
  const operationState = useAppSelector(state => state.operation);
  const operation = operationState.operation;
  const storage = useAppSelector(x => x.storage);

  const { removeSingleJobMenu, goBackSingle } = useJobMenus();
  const getExternalFunctions = useFunction({
    callbacks: ['ifmsSettingsSave', 'ifmsSettingsCancel', 'ifmsReferenceFilesSave', 'ifmsReferenceFilesCancel', /*'ifmsMyTaskSave', 'ifmsMyTaskCancel'*/]
  });

  let operationKey = getOperationKey(operation);
  if (!operationKey) {
    if (operation === 'Reference Files' || operation === 'Settings') {
      operationKey = getOperationKey('Standalone');
    }
  }

  const jobMenuItems = !!operationKey
    ? Object.keys(jobMenu)
      .flatMap(opKey => jobMenu[opKey].map(item => {
        const op = getOpByName(opKey);
        let suffixlabel = '';
        if (!item.label.trim().startsWith('Container List')) {  // Container List의 경우 getListViewTitle() 값을 그대로 사용하도록 적용
          if (op && !item.noOperationSuffix && item.jobType && !!item.addTypeSuffix) {
            suffixlabel = `(${op} ${item.jobType})`;
          } else if (op && !item.noOperationSuffix) {
            suffixlabel = `(${op})`;
          } else if (item.jobType && !!item.addTypeSuffix) {
            suffixlabel = `(${item.jobType})`;
          }
        }
        return {
          ...item,
          label: `${item.label} ${suffixlabel}`,
        };
      }))
      .sort((a, b) => {
        if (a?.timestamp === b?.timestamp) {
          return 0;
        } else {
          return a?.timestamp! > b?.timestamp! ? 1 : -1;
        }
      })
    : [];

  const isStandaloneOperation = operation === constants.operations.standalone;
  if (isStandaloneOperation) {
    if (jobMenuItems.length > 0) {
      const standaloneTitle: string = jobMenuItems[0].label;
      chkChannel.publish('ifms.standalone-title', standaloneTitle);
    }
  }

  const [fixedItem, ...scrollableItems] = jobMenuItems;
  const renderingJobMenuItems = scrollableItems;

  const buttonsRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(false);

  const menuContainerRef = useRef<HTMLDivElement>(null);
  const sizes = getActualWidths(renderingJobMenuItems.length, screenWidth - buttonWidth, context.isHome);

  const activeIndexInView = jobMenuItems.findIndex(j => `${history.location.pathname}${history.location.search}` === j.url);

  useEffect(() => {
    setMouseOverBtnNum(-1);
  }, [history.location.pathname])

  useEffect(() => {
    if (activeIndexInView > 0) {
      const scrollableIndex = activeIndexInView - 1;
      scrollJobMenuItemIntoView({ ...renderingJobMenuItems[scrollableIndex], index: scrollableIndex });
    }
    // else if (activeIndexInView === 0) {
    //   if (buttonsRef && buttonsRef.current) {
    //     buttonsRef.current.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    //     currentLeftRef.current = 0;
    //     currentScrollPositionRef.current = 0;
    //   }
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndexInView]);

  useEffect(() => {
    const el = buttonsRef.current;
    if (!el) return;
  
    const SCROLL_SPEED = 0.35;

    const handleWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
  
      const rawDelta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (Math.abs(rawDelta) < 0.1) return;
  
      const delta = rawDelta * SCROLL_SPEED;
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      const newScrollLeft = el.scrollLeft + delta;
  
      const canScrollLeft = delta < 0 && el.scrollLeft > 0;
      const canScrollRight = delta > 0 && el.scrollLeft < maxScrollLeft;
  
      if (canScrollLeft || canScrollRight) {
        el.scrollLeft = Math.max(0, Math.min(newScrollLeft, maxScrollLeft));
        currentLeftRef.current = el.scrollLeft;
        e.preventDefault();
      }
    };
  
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    const container = buttonsRef?.current;
    if (!container) return;

    container.addEventListener('scroll', updateScrollBtnStates);
    updateScrollBtnStates();

    return () => container.removeEventListener('scroll', updateScrollBtnStates);
  }, [jobMenuItems.length]);

  function updateScrollBtnStates() {
    const container = buttonsRef?.current;
    if (!container) {
      setDisableJobSwitcgLeftBtn(true);
      setDisableJobSwitcgRightBtn(true);
      return;
    }
    setDisableJobSwitcgLeftBtn(container.scrollLeft <= 1);
    setDisableJobSwitcgRightBtn(
      container.scrollLeft + container.clientWidth >= container.scrollWidth - 1
    );
  }

  function scrollLeft() {
    const container = buttonsRef?.current;
    if (!container) return;

    const gap = 5;
    const items = Array.from(container.querySelectorAll<HTMLElement>('.ifms-job-btn-container:not(.ifms-fixed-item)'));
    if (items.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const containerScrollLeft = container.scrollLeft;

    let newLeft = 0;
    for (let i = items.length - 1; i >= 0; i--) {
      const itemRect = items[i].getBoundingClientRect();
      const itemLeft = itemRect.left - containerRect.left + containerScrollLeft;

      if (itemLeft < containerScrollLeft - 1) {
        newLeft = itemLeft; 
        break;
      }
    }

    container.scrollTo({ left: newLeft, top: 0, behavior: 'smooth' });
    currentLeftRef.current = newLeft;
    currentScrollPositionRef.current = calcScrollStart(items, newLeft, gap);
  }

  function scrollRight() {
    const container = buttonsRef?.current;
    if (!container) return;

    const gap = 5;
    const items = Array.from(
      container.querySelectorAll<HTMLElement>('.ifms-job-btn-container:not(.ifms-fixed-item)')
    );
    if (items.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const containerScrollLeft = container.scrollLeft;
    const containerRight = containerScrollLeft + container.clientWidth;

    let newLeft = containerScrollLeft;
    for (let i = 0; i < items.length; i++) {
      const itemRect = items[i].getBoundingClientRect();
      const itemRight = itemRect.right - containerRect.left + containerScrollLeft;

      if (itemRight > containerRight + 1) {
        newLeft = itemRight - container.clientWidth;
        break;
      }
    }

    container.scrollTo({ left: newLeft, top: 0, behavior: 'smooth' });
    currentLeftRef.current = newLeft;
    currentScrollPositionRef.current = calcScrollStart(items, newLeft, gap);
  }

  function calcScrollStart(items: HTMLElement[], newLeft: number, gap: number): number {
    const widths = items.map(el => el.clientWidth + gap);
    const prefix = [0];
    for (let i = 0; i < widths.length; i++) prefix.push(prefix[i] + widths[i]);

    let newStart = 0;
    for (let i = 0; i < prefix.length - 1; i++) {
      if (prefix[i] <= newLeft) newStart = i;
      else break;
    }
    return Math.min(newStart, Math.max(0, items.length - 1));
  }

  function scrollJobMenuItemIntoView(j: JobMenuItemWithIndex) {
    const container = buttonsRef?.current;
    if (!container) return;
  
    const items = Array.from(container.querySelectorAll<HTMLElement>('.ifms-job-btn-container:not(.ifms-fixed-item)'));
    if (items.length === 0) return;
  
    const targetIndex = j.index;
    if (targetIndex < 0 || targetIndex >= items.length) return;
  
    const gap = 5;
    const available = Math.max(0, sizes.actualWidth - sizes.scrollControlsWidth);
    if (available <= 0) return;

    const widths = items.map(el => el.clientWidth + gap);
    const prefix: number[] = new Array(widths.length + 1);
    prefix[0] = 0;
    for (let i = 0; i < widths.length; i++) prefix[i + 1] = prefix[i] + widths[i];
  
    let batchSize = 0;
    while (batchSize < widths.length && prefix[batchSize + 1] <= available) batchSize++;
    if (batchSize === 0) batchSize = 1;
    batchSize = Math.min(batchSize, widths.length);
  
    const maxStart = Math.max(0, items.length - batchSize);
    let start = Math.min(Math.max(0, currentScrollPositionRef.current), maxStart);

    if (targetIndex < start) {
      start = targetIndex;
    } else if (targetIndex > start + batchSize - 1) {
      start = Math.min(targetIndex - batchSize + 1, maxStart);
    }

    const targetEl = items[targetIndex];
    const containerRect = container.getBoundingClientRect();
    const itemRect = targetEl.getBoundingClientRect();

    const itemLeft = itemRect.left - containerRect.left + container.scrollLeft;
    const itemRight = itemRect.right - containerRect.left + container.scrollLeft;
  
    const containerLeft = container.scrollLeft;
    const containerRight = container.scrollLeft + container.clientWidth;
  
    let newLeft = containerLeft;
  
    if (itemLeft < containerLeft) {
      newLeft = itemLeft;
    } else if (itemRight > containerRight) {
      newLeft = itemRight - container.clientWidth;
    } else {
      newLeft = containerLeft;
    }

    if (Math.abs(newLeft - currentLeftRef.current) > 1) {
      container.scrollTo({ left: newLeft, top: 0, behavior: 'smooth' });
      currentLeftRef.current = newLeft;
    }

    let newStart = 0;
    for (let i = 0; i < prefix.length; i++) {
      if (prefix[i] <= newLeft) newStart = i;
      else break;
    }
    newStart = Math.min(newStart, maxStart);
    currentScrollPositionRef.current = newStart;
  }

  function setMenuHelperVisible(v: boolean) {
    openRef.current = v;
    _setMenuHelperVisible(v);
  }

  function isScreenDirty(urlParam: string): boolean {
    let result = false;

    //const url = !urlParam.includes('common/joblistview') ? getRoutePath(urlParam) : urlParam;

    _.mapKeys(fastStorage, (val, key) => {
      if (result) {
        return;
      }

      //if (!key.endsWith(url)) {
      if (!key.endsWith(urlParam)) {
        return;
      }

      if (val && val.isDirty !== undefined && val.isDirty === true) {
        result = true;
      }
    })

    return result;
  }

  async function checkStandaloneDirty() {
    //if(operationState.operation !== 'Standalone') return;
    if (!['Standalone', 'My Task'].includes(operationState.operation)) return;

    if (fastStorage['ifms-settings-haschanges']) {
      const doSave = await showGlobalYesNoDialog(
        <div>
          There are unsaved changes. Do you want to save?
        </div>
        , false, 1, true, 'info');

      const externalFunctions = getExternalFunctions();
      if (doSave) {
        await externalFunctions.ifmsSettingsSave();
      } else {
        await externalFunctions.ifmsSettingsCancel();
      }
      fastStorage['ifms-settings-haschanges'] = false;
    }
    else if (fastStorage['ifms-referenceFiles-haschanges']) {
      const doSave = await showGlobalYesNoDialog(
        <div>
          There are unsaved changes. Do you want to save?
        </div>
        , false, 1, true, 'info');

      const externalFunctions = getExternalFunctions();
      if (doSave) {
        await externalFunctions.ifmsReferenceFilesSave();
      } else {
        await externalFunctions.ifmsReferenceFilesCancel();
      }
      fastStorage['ifms-referenceFiles-haschanges'] = false;
    }
    // else if (fastStorage['ifms-myTask-haschanges']) {
    //   const doSave = await showGlobalYesNoDialog(
    //     <div>
    //       There are unsaved changes. Do you want to save?
    //     </div>
    //     , false, 1, true, 'info');

    //   const externalFunctions = getExternalFunctions();
    //   if (doSave) {
    //     await externalFunctions.ifmsMyTaskSave();
    //   } else {
    //     await externalFunctions.ifmsMyTaskCancel();
    //   }
    // }
  }

  async function executeJobMenu(j: JobMenuItem) {
    await checkStandaloneDirty();

    resetJobMenuContextState();

    let afterScreenUrl = j.afterScreenUrl;
    if (j.url) {
      if (context.isHome) {
        dispatch(changeSideMenu('Home'));
        dispatch(changeOperation('Home'));
      }

      if (operationKey) {
        if (['invoice/view', 'bill/view', 'crdb/view', 'periodInvoice/view'].some(x => j.url.indexOf(x) >= 0)) {
          const lastScreenQuery = getSingleOpenedScreenInAnyOperationStartingWithUrl(j.url);
          if (lastScreenQuery && lastScreenQuery.item) {
            afterScreenUrl = lastScreenQuery.operation;
          }
        }

        dispatch(moveScreenToTop({
          url: j.url,
          operation: afterScreenUrl ?? operationKey,
        }));
      }

      openNewPage(history, j.url, NavigationType.jobMenuSwitch, j.metadata, afterScreenUrl);
    }
  }

  function isChkMemo(urlParam: string): number {
    let id = 0;
    _.mapKeys(fastStorage, (val, key) => {

      if (!key.endsWith(urlParam)) {
        return;
      }

      if (val && val.id <= 0) {
        id = val.id;
      }
    })

    return id;
  }

  function jobMenuSelectedFromHelperMenu(j: IfmsMenuItem) {
    setMenuHelperVisible(false);
    if (j && j.job) {
      executeJobMenu(j.job);
    }
  }

  async function purgeLocalStorage() {
    const keys = await localforage.keys();
    const toDelete = keys.filter(k => k.startsWith('filter-'));

    for (const key of toDelete) {
      await localforage.removeItem(key);
    }

    const keysToRemove = Object.keys(fastStorage).filter(k => (
      k.startsWith('filter-') ||
      k.startsWith('ifms-list-data-') ||
      k.includes('referenceFiles') ||
      k.includes('settings') ||
      k.includes('myTask')
    ));

    keysToRemove.forEach(k => delete fastStorage[k]);
  }

  async function resetScreens(e: SyntheticEvent) {
    function distinct(value: JobMenuItem, index: number, self: Array<JobMenuItem>) {
      return self.findIndex(x => stringsEqualIgnoreCase(x.url, value.url)) === index;
    }

    const allJobMenuItems = [
      ...jobMenu.home,
      ...jobMenu.oceanImport,
      ...jobMenu.oceanExport,
      ...jobMenu.airImport,
      ...jobMenu.airExport,
      ...jobMenu.otherShipment,
      ...jobMenu.whReceipt,
      ...jobMenu.accounting,
      ...jobMenu.warehousing,
      ...jobMenu.opReport,
      ...jobMenu.customer,
      ...jobMenu.standalone,
      ...jobMenu.utility,
      ...jobMenu.billings,
      ...jobMenu.myEvent,
      ...jobMenu.businessIntell,
      ...jobMenu.ams,
      ...jobMenu.isf,
      ...jobMenu.myTask,
      ...jobMenu.shipmentStatus,
      ...jobMenu.cntr,
      ...jobMenu.aes,
      ...jobMenu.aiCenter,
    ].filter(x => !stringsEqualIgnoreCase('Home', x.label)).filter(distinct)

    const jobMenuItemsToClose = allJobMenuItems.filter(x => !x.jobMode || x.jobMode === 'view');
    let doClose = false;

    if (jobMenuItemsToClose.length > 0) {
      doClose = await showGlobalYesNoDialog(
        <div>
          <h3>Are you sure you want to close all tabs except edit and new view?</h3>
        </div>
        , false, 0, true);
    }

    if (doClose) {
      channel.publish('ajax.loadingstart', {});
      await purgeLocalStorage();

      jobMenuItemsToClose.forEach(async x => {
        currentScrollPositionRef.current = 0;
        await closeJobMenuTab(e, x.url, x.operationName || '', false);
      });
      const exceptedJobMenuItems = allJobMenuItems
        .filter(x => x.jobMode && x.jobMode !== 'view')
        .sort((a, b) => {
          const timeA = new Date(a.timestamp ?? '').getTime();
          const timeB = new Date(b.timestamp ?? '').getTime();
          return timeA - timeB;
        });

      if (exceptedJobMenuItems.length > 0)
        executeJobMenu(exceptedJobMenuItems[exceptedJobMenuItems.length - 1])
      else {
        dispatch(clearStorage());
        dispatch(clearOperation());
        dispatch(resetListScreenSettings());
        dispatch(clearLastSavedJobs());
        history.push('/');
        openNewPage(history, '/home/home', NavigationType.newPage);
      }
      channel.publish('ajax.loadingend', {});
    }
  }

  function getJobMenuLabel(j: JobMenuItem, index: number) {
    if (!j || !j.url) return;
    // In case job menu represents list screen, and list screen has filtering applied, add additional icon to the label
    // In case filter is external set (F6 for example MBL -> HBL), treat it as no filter
    let filter = <></>;

    const otherStorageKey = `filter-${FilterStorageVersion}-${j.url}`;
    const screenFilter = storage[otherStorageKey];

    // operationKey가 'standalone'인 경우 Filter 버튼은 내부 Tab 별로 그려줌
    if ((!isFilterObjectEmpty(screenFilter) || screenFilter?.isSearchByReport) && j.operationName !== 'Standalone' || screenFilter?.listType > 0 ||screenFilter?.listType2 > 0 ||screenFilter?.listType3 > 0 ||screenFilter?.listType4 > 0) {
      filter = <div>(
        <TooltipButton
          jobMenuLabel={j.label}
          jobMenuMetadata={j.metadata}
          jobMenuUrl={j.url}
          screenFilter={screenFilter}
          isActiveMenu={`${history.location.pathname}${history.location.search}` === j.url}>
        </TooltipButton>)
      </div>;
    }

    return (
      <div style={{ width: '100%', height: '20px', display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', width: '100%', marginLeft: '5px', marginRight: '5px', minWidth: '99px' }}>
          {j.url !== '/home/home' ? <span>{j.label}</span> : 
            <><span>{j.label}</span><TaskReminderInfo/></>
          }
          <>{filter}</>
        </div>

        {j.url !== '/home/home' && (
          <div id={`job-menu-close-${j.label.replace(/ /g, '')}`} className='ifms-job-btn-close-wrapper' onClick={(e) => closeJobMenuTab(e, j.url, j.operationName || '', true, j.jobMode, j.jobType)}>
            <img style={{ width: '18px' }} src={mouseOverBtnNum === index ? circleExitWhite : circleExitRed} alt='' />
          </div>
        )}
      </div>
    );
  }

  function getJobMenuInactiveClass(j: JobMenuItem) {
    return !!j.operationName ? `${!useDarkMode ? 'e-job-btn-inactive' : 'e-job-btn-inactive-darkmode'}` : 'e-job-btn-common-inactive';
  }

  function getJobMenuActiveClass(j: JobMenuItem) {
    return !!j.operationName ? `${!useDarkMode ? 'e-job-btn-active' : 'e-job-btn-active-darkmode'}` : 'e-job-btn-common-active';
  }

  function getJobMenuClass(j: JobMenuItem) {
    return `e-ifms-jsmbtn e-job-btn ${`${history.location.pathname}${history.location.search}` !== j.url ? getJobMenuInactiveClass(j) : getJobMenuActiveClass(j)}`;
  }

  const appliedPopperStyles: React.CSSProperties = {
    ...styles.popper,
    minWidth: '150px',
    paddingTop: 10,
  };


  const closeJobMenuTab = async (e: SyntheticEvent, url: string, urlOperation: string, needGoBack: boolean = true, jobMode: string = '', jobType: string = '') => {
    let id = 0;
    e.stopPropagation();
    // if (!jobMode || jobMode !== 'view') {
    //   if (isScreenDirty(url) && !suntekSettings.general?.showCloseWithoutSavingMsg) {
    //     const doClose = await showGlobalYesNoDialog(
    //       <div>
    //         <h3>Do you want to Close without saving?</h3>
    //       </div>
    //       , false, 0, true);

    //     if (!doClose) {
    //       return;
    //     }
    //   }
    // }


    if (url.includes('/common/joblistview/RPCSP/shipmentStatus')) {
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('shipStatus-cust-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
      const keyMatch2 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-status-ListFilter'));
      if (keyMatch2) {
        delete fastStorage[keyMatch2];
      }
      const keyMatch3 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-tracking-ListFilter'));
      if (keyMatch3) {
        delete fastStorage[keyMatch3];
      }
      const keyMatch4 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-blType-ListFilter'));
      if (keyMatch4) {
        delete fastStorage[keyMatch4];
      }
    }

    if(url.includes('/common/joblistview/AICTR/aiCenter')){
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('aiCenter-result-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
    }

    //Payment Tab에서 X 버튼 클릭했을때 메모 Delete 별도 처리
    if (jobMode === 'add' && jobType === 'DEP') {
      id = isChkMemo(url);
      if (id < 0) {
        await deleteMemo(appContext, FormType.ModeAdd, id, "T_DEPOHD");
      }
    }

    resetJobMenuContextState();

    if (url === `${history.location.pathname}${history.location.search}` && needGoBack) {
      await goBackSingle(url, urlOperation)
    } else {
      removeSingleJobMenu(url, urlOperation);
    }
  }

  return (
    <div id={`${!useDarkMode ? 'ifms-job-switching-menu-master-container' : 'ifms-job-switching-menu-master-container-darkmode'}`}>
      <div className={!useDarkMode ? 'ifms-job-switching-menu' : 'ifms-job-switching-menu-darkmode'}>
        <div style={{ display: 'flex' }}>
          <div className="ifms-job-btn-container ifms-fixed-item" key={fixedItem.url}>
            <div
              style={{ paddingTop: '3px' }}
              className={getJobMenuClass(fixedItem)}
              id={`job-menu-${fixedItem.label.replace(/ /g, '')}`}
              onMouseEnter={() => { setMouseOverBtnNum(0) }}
              onMouseLeave={() => { setMouseOverBtnNum(-1) }}
              onClick={() => executeJobMenu(fixedItem)}
            >
              {getJobMenuLabel(fixedItem, 0)}
            </div>
          </div>

          <div ref={buttonsRef} id="ifms-job-menu-link-buttons-container" style={{ width: sizes.actualWidth - sizes.scrollControlsWidth }}>
            <div id="ifms-job-menu-link-buttons">
            {renderingJobMenuItems.map((j, index) => (
              <div className="ifms-job-btn-container" key={j.url}>
                <div
                  style={{ paddingTop: '3px' }}
                  className={getJobMenuClass(j)}
                  id={`job-menu-${j.label.replace(/ /g, '')}`}
                  onMouseEnter={() => setMouseOverBtnNum(index + 1)}
                  onMouseLeave={() => setMouseOverBtnNum(-1)}
                  onClick={() => {
                    if (currentUrl !== j.url) {
                      executeJobMenu(j);
                    }
                  }}
                >
                  {getJobMenuLabel(j, index + 1)}
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>

        <div id="ifms-job-menu-controls">
          <PrimaryButton
            name='list-memoLog'
            size="extrasmall"
            onClick={() => {
              appContext.setAppInfoAreaContent(appContext.appInfoAreaContent === 'memo' ? 'none' : 'memo');
            }}
            style={{ marginRight: '10px' }}
          >Doc. Server
          </PrimaryButton>

          <img id="closeAllTab"
            onClick={e => { resetScreens(e) }}
            height="18px" width='40px'
            src={circleExitRed}
            alt="Close All Tabs"
            data-tooltip="Close All Tabs"
            style={{ cursor: 'pointer', marginTop: '1px' }} />

          {disableJobSwitcgLeftBtn ?
            <img height='28px' width='28px' src={arrowLefGrayIcon} style={{ opacity: useDarkMode ? 1 : 0.3 }}/>
            :
            <img onClick={scrollLeft} height='28px' width='28px' src={useDarkMode ? arrowLeftSkyBlueIcon : arrowLeftBlueIcon} style={{ cursor: 'pointer' }} />
          }
          {disableJobSwitcgRightBtn ?
            <img height='28px' width='28px' src={arrowRightGrayIcon} style={{ opacity: useDarkMode ? 1 : 0.3 }} />
            :
            <img onClick={scrollRight} height='28px' width='28px' src={useDarkMode ? arrowRightSkyBlueIcon : arrowRightBlueIcon} style={{ cursor: 'pointer' }} />
          }
          <img
            onMouseLeave={() => setMenuHelperVisible(false)}
            onMouseEnter={() => setMenuHelperVisible(true)}
            height="27px"
            width='40px'
            src={appContext.useDarkMode ? tabListIcon_D : tabListIcon}
            style={{ cursor: 'pointer' }}
            alt=""
          />
          <span ref={setReferenceElement}>
          </span>
          {menuHelperVisible &&
            <div
              onMouseMove={(e) => { e.stopPropagation() }}
              onMouseEnter={() => setMenuHelperVisible(true)}
              onMouseLeave={() => setMenuHelperVisible(false)}
              ref={setPopperElement} style={appliedPopperStyles} {...attributes.popper}>
              <div ref={menuContainerRef}>
                <Menu
                  specialMenu='jobswitchinghelper'
                  items={[[jobMenuItems.map((j, index) => ({
                    text: `${index + 1}. ${j.label}`,
                    job: j,
                    selected: `${history.location.pathname}${history.location.search}` === j.url,
                    index,
                  }))]]}
                  // variant='highlight'
                  mode="vertical"
                  onMenuItemSelected={jobMenuSelectedFromHelperMenu}
                  subMenuStyle={{
                    minWidth: 210
                  }}
                  menuStyle={(i: number) => ({
                    maxHeight: 500,
                    zIndex: 214748364,
                    padding: 0,
                    overflowY: 'auto',
                    // border:'1px solid #595959',
                    border: useDarkMode ? '3px solid #6C747E' : '3px solid #7DBBFE',
                    borderRadius: '5px',
                    backgroundColor: useDarkMode ? '#141414' : '#E0FFFF',
                  })}
                />
                <div ref={setArrowElement} style={styles.arrow} />
              </div>
            </div>
          }
        </div>
        {/* } */}

        <div className="clearfix" />
      </div>
    </div>
  );
};

JobSwitchingMenu.defaultProps = {
};

JobSwitchingMenu.propTypes = {
  resetJobMenuContextState: PropTypes.func.isRequired,
};

export default React.memo(JobSwitchingMenu);
